// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { useEffect, useState, useCallback, useMemo, memo } from "react";
import {
  getDocs,
  addDoc,
  query,
  where,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { auth } from "../firebase";
import { useClient } from "../contexts/ClientContext";
import { getIdTokenResult } from "firebase/auth";
import {
  Box,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
  Typography,
  Snackbar,
  Alert,
  SelectChangeEvent,
  Card,
  CardContent,
  Paper,
  Divider,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  TablePagination,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TodayIcon from "@mui/icons-material/Today";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SaveIcon from "@mui/icons-material/Save";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { PickersCalendarHeaderProps } from "@mui/x-date-pickers/PickersCalendarHeader";
import dayjs, { Dayjs } from "dayjs";
import usePagination from "../hooks/usePagination";
import { timed, perfMark, perfTotal } from "../utils/perf";

interface Location {
  id: string;
  name: string;
}
interface Employee {
  empId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  locationId?: string;
  branchId?: string;
  status?: string;
}
interface AttendanceRow {
  id: string;
  empId: string;
  branchId: string;
  date: string;
  clockIn: Timestamp | null;
  clockOut: Timestamp | null;
  lunchOut: Timestamp | null;
  lunchIn: Timestamp | null;
  verified: boolean;
}

/* ── Bulk row type ── */
interface BulkRow {
  empId: string;
  empName: string;
  clockInTime: string;
  clockOutTime: string;
  lunchOutTime: string;
  lunchInTime: string;
  existingId: string | null; // existing attendance doc id
  hasExisting: boolean;
}

const LS_LAST_LOC = "starone_lastLocationId";
const LS_LAST_EMP = "starone_lastEmpId";

type DateView = "day" | "month" | "year";

const SmartCalendarHeader = ({
  currentMonth,
  onMonthChange,
  onViewChange,
  view,
}: PickersCalendarHeaderProps) => (
  <Box sx={{ display: "flex", alignItems: "center", px: 1, py: 0.5, minHeight: 44 }}>
    {view === "day" ? (
      <>
        <IconButton size="small" onClick={() => onMonthChange(currentMonth.subtract(1, "month"))}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", gap: 0 }}>
          <Button size="small" onClick={() => onViewChange?.("month")} sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.875rem", minWidth: 0, px: 0.75 }}>
            {currentMonth.format("MMMM")}
          </Button>
          <Button size="small" onClick={() => onViewChange?.("year")} sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.875rem", minWidth: 0, px: 0.75 }}>
            {currentMonth.format("YYYY")}
          </Button>
        </Box>
        <IconButton size="small" onClick={() => onMonthChange(currentMonth.add(1, "month"))}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </>
    ) : (
      <>
        <IconButton size="small" onClick={() => onViewChange?.("day")} sx={{ mr: 1 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700, color: "text.secondary" }}>
          {view === "year" ? "Select Year" : "Select Month"}
        </Typography>
      </>
    )}
  </Box>
);

interface BulkDatePickerProps {
  value: string;
  onChange: (val: string) => void;
  minDate: Dayjs;
  maxDate: Dayjs;
}

const BulkDatePicker = memo(function BulkDatePicker({ value, onChange, minDate, maxDate }: BulkDatePickerProps) {
  const dayjsValue = useMemo(() => dayjs(value), [value]);
  const [calView, setCalView] = useState<DateView>("day");
  return (
    <DatePicker
      label="Date"
      value={dayjsValue}
      onChange={(v: Dayjs | null) => { if (v) { onChange(v.format("YYYY-MM-DD")); setCalView("day"); } }}
      onViewChange={(v) => setCalView(v as DateView)}
      view={calView}
      views={["year", "month", "day"]}
      format="DD/MMM/YYYY"
      maxDate={maxDate}
      minDate={minDate}
      slots={{ calendarHeader: SmartCalendarHeader }}
      slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
    />
  );
});

export default function ReceptionistPage() {
  const { col, docRef, timesheetEditDays } = useClient();
  const [locations, setLocations] = useState<Location[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [locationId, setLocationId] = useState("");
  const [empId, setEmpId] = useState("");
  const [leaveRequests, setLeaveRequests] = useState<{ empId: string; startDate: string; endDate: string; status: string }[]>([]);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"success" | "error" | "info">("success");
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [todayRecords, setTodayRecords] = useState<AttendanceRow[]>([]);
  const [role, setRole] = useState("");
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeTarget, setChangeTarget] = useState<AttendanceRow | null>(null);
  const [changeReason, setChangeReason] = useState("");
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [customDate, setCustomDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [customTime, setCustomTime] = useState(dayjs().format("HH:mm"));
  // Confirmation dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"clockIn" | "clockOut" | null>(null);
  // View mode: individual or bulk
  const [viewMode, setViewMode] = useState(1); // 0=individual, 1=bulk
  // Bulk update state
  const [bulkRows, setBulkRows] = useState<BulkRow[]>([]);
  const [originalBulkRows, setOriginalBulkRows] = useState<BulkRow[]>([]);
  const [bulkDate, setBulkDate] = useState(dayjs().subtract(1, "day").format("YYYY-MM-DD"));
  const tsMinDate = useMemo(() => dayjs().subtract(timesheetEditDays, "day"), [timesheetEditDays]);
  const tsMaxDate = useMemo(() => dayjs(), []);
  const [bulkSaving, setBulkSaving] = useState(false);
  const bulkPag = usePagination(bulkRows);
  const [pendingBulkPage, setPendingBulkPage] = useState<{ page: number; rpp?: number } | null>(null);
  const recPag = usePagination(todayRecords);

  const today = dayjs().format("YYYY-MM-DD");

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Get user role
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      getIdTokenResult(user).then((r) => setRole((r.claims.role as string) ?? ""));
    }
  }, []);

  const recordsDate = viewMode === 1 ? bulkDate : today;

  const loadTodayRecords = useCallback(async () => {
    try {
      const q = query(col("attendance"), where("date", "==", recordsDate));
      const snap = await timed(`ReceptionistPage.loadRecords(${recordsDate})`, () => getDocs(q));
      setTodayRecords(snap.docs.map((d) => {
        const data = d.data() as Record<string, unknown>;
        return {
          id: d.id,
          empId: (data.empId as string) ?? "",
          branchId: (data.branchId as string) ?? "",
          date: (data.date as string) ?? "",
          clockIn: (data.clockIn as Timestamp) ?? null,
          clockOut: (data.clockOut as Timestamp) ?? null,
          lunchOut: (data.lunchOut as Timestamp) ?? null,
          lunchIn: (data.lunchIn as Timestamp) ?? null,
          verified: (data.verified as boolean) ?? false,
        };
      }));
    } catch {
      /* silently fail */
    }
  }, [recordsDate]);

  useEffect(() => {
    async function load() {
      const t0 = performance.now();
      try {
        const [brSnap, empSnap, lrSnap] = await Promise.all([
          timed("ReceptionistPage.load.branches", () => getDocs(col("branches"))),
          timed("ReceptionistPage.load.employees", () => getDocs(col("employees"))),
          timed("ReceptionistPage.load.leaveRequests", () => getDocs(col("leaveRequests"))),
        ]);

        const locs = brSnap.docs.map((d) => ({ id: d.id, name: d.data().name as string })).sort((a, b) => {
          if (a.name.toLowerCase().startsWith("anakapalli")) return -1;
          if (b.name.toLowerCase().startsWith("anakapalli")) return 1;
          return a.name.localeCompare(b.name);
        });
        setLocations(locs);

        setEmployees(
          empSnap.docs.map((d) => {
            const data = d.data();
            return {
              empId: d.id,
              firstName: (data.firstName as string) || "",
              lastName: (data.lastName as string) || "",
              fullName: (data.fullName as string) || `${data.firstName || ""} ${data.lastName || ""}`.trim() || d.id,
              locationId: (data.branchId as string) || "",
              branchId: (data.branchId as string) || "",
              status: (data.status as string) || "active",
            };
          })
        );

        // Default location: restore last used, or auto-select Anakapalli (individual mode only)
        if (viewMode !== 1) {
          const lastLoc = localStorage.getItem(LS_LAST_LOC);
          if (lastLoc && locs.some((l) => l.id === lastLoc)) {
            setLocationId(lastLoc);
          } else {
            const anakapalli = locs.find((l) => l.name.toLowerCase().includes("anakapalli"));
            if (anakapalli) setLocationId(anakapalli.id);
          }
        }
        // Restore last used employee
        const lastEmp = localStorage.getItem(LS_LAST_EMP);
        if (lastEmp) setEmpId(lastEmp);

        setLeaveRequests(
          lrSnap.docs.map((d) => {
            const data = d.data();
            return {
              empId: (data.empId as string) || "",
              startDate: (data.startDate as string) || "",
              endDate: (data.endDate as string) || "",
              status: (data.status as string) || "",
            };
          })
        );

        perfTotal("ReceptionistPage.load TOTAL", t0);
      } catch {
        showMsg("Failed to load data. Please refresh.", "error");
      }
    }
    load();
    loadTodayRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTodayRecords]);

  const showMsg = (text: string, type: "success" | "error" | "info" = "success") => {
    setMsg(text);
    setMsgType(type);
  };

  const getEmpName = (id: string) => {
    const emp = employees.find((e) => e.empId === id);
    return emp ? (emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : emp.fullName) : id;
  };

  // Sort locations: last used first
  const sortedLocations = (() => {
    const lastLoc = localStorage.getItem(LS_LAST_LOC);
    if (!lastLoc) return locations;
    return [...locations].sort((a, b) => (a.id === lastLoc ? -1 : b.id === lastLoc ? 1 : 0));
  })();

  // Sort employees: last used first
  const filteredEmployees = (() => {
    const active = employees.filter((e) => !e.status || e.status === "active");
    const filtered = locationId
      ? active.filter((e) => (e.branchId || e.locationId) === locationId)
      : active;
    const lastEmp = localStorage.getItem(LS_LAST_EMP);
    if (!lastEmp) return filtered;
    return [...filtered].sort((a, b) => (a.empId === lastEmp ? -1 : b.empId === lastEmp ? 1 : 0));
  })();

  const getSelectedTimestamp = (): Timestamp => {
    if (!useCustomTime) return Timestamp.now();
    const dt = dayjs(`${customDate} ${customTime}`, "YYYY-MM-DD HH:mm");
    return Timestamp.fromDate(dt.toDate());
  };
  const getSelectedDate = (): string => useCustomTime ? customDate : today;
  const getSelectedTimeLabel = (): string =>
    useCustomTime ? dayjs(`${customDate} ${customTime}`, "YYYY-MM-DD HH:mm").format("hh:mm A") : dayjs().format("hh:mm:ss A");

  // Persist last used location/employee
  const handleLocationChange = (val: string) => {
    setLocationId(val);
    setEmpId("");
    if (val) localStorage.setItem(LS_LAST_LOC, val);
  };
  const handleEmpChange = (val: string) => {
    setEmpId(val);
    if (val) localStorage.setItem(LS_LAST_EMP, val);
  };

  // Check if employee is on leave for a given date
  const isOnLeave = (eid: string, date: string): boolean =>
    leaveRequests.some(
      (lr) =>
        lr.empId === eid &&
        (lr.status === "approved" || lr.status === "pending") &&
        date >= lr.startDate &&
        date <= lr.endDate
    );

  // Confirmation-wrapped clock in/out
  const requestClockIn = () => {
    if (!empId) return showMsg("Please select an employee before clocking in.", "error");
    setConfirmAction("clockIn");
    setConfirmOpen(true);
  };
  const requestClockOut = () => {
    if (!empId) return showMsg("Please select an employee to clock out.", "error");
    setConfirmAction("clockOut");
    setConfirmOpen(true);
  };
  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (confirmAction === "clockIn") await doClockIn();
    else if (confirmAction === "clockOut") await doClockOut();
    setConfirmAction(null);
  };

  const doClockIn = async () => {
    const selectedDate = getSelectedDate();
    if (selectedDate > today) return showMsg("Cannot clock in for a future date.", "error");
    if (dayjs(today).diff(dayjs(selectedDate), "day") > timesheetEditDays) return showMsg(`Cannot clock in for dates older than ${timesheetEditDays} days.`, "error");
    if (isOnLeave(empId, selectedDate)) return showMsg("This employee has an approved/pending leave for this date. Timesheet entry not allowed.", "error");
    const branchId = locationId || employees.find((e) => e.empId === empId)?.branchId || "";
    const done = perfMark(`ReceptionistPage.clockIn(${getEmpName(empId)})`);
    try {
      const dupQ = query(
        col("attendance"),
        where("empId", "==", empId),
        where("date", "==", selectedDate),
        where("clockOut", "==", null)
      );
      const dupSnap = await getDocs(dupQ);
      if (!dupSnap.empty) return showMsg("This employee already has an open clock-in without clock-out. Please clock out first.", "error");
      await addDoc(col("attendance"), {
        empId,
        branchId,
        date: selectedDate,
        clockIn: getSelectedTimestamp(),
        clockOut: null,
        lunchOut: null,
        lunchIn: null,
        createdBy: auth.currentUser?.uid ?? "",
        verified: false,
      });
      done();
      showMsg(`Clock-In recorded for ${getEmpName(empId)} at ${getSelectedTimeLabel()}`);
      loadTodayRecords();
    } catch {
      showMsg("Failed to record Clock-In. Please try again.", "error");
    }
  };

  const doClockOut = async () => {
    const selectedDate = getSelectedDate();
    if (selectedDate > today) return showMsg("Cannot clock out for a future date.", "error");
    if (dayjs(today).diff(dayjs(selectedDate), "day") > timesheetEditDays) return showMsg(`Cannot clock out for dates older than ${timesheetEditDays} days.`, "error");
    const done = perfMark(`ReceptionistPage.clockOut(${getEmpName(empId)})`);
    try {
      const q = query(
        col("attendance"),
        where("empId", "==", empId),
        where("date", "==", selectedDate),
        where("clockOut", "==", null)
      );
      const snap = await getDocs(q);
      if (snap.empty) return showMsg(`No open Clock-In found for this employee on ${dayjs(selectedDate).format("DD/MMM/YYYY")}.`, "error");
      const clockInTs = snap.docs[0].data().clockIn as Timestamp;
      const clockOutTs = getSelectedTimestamp();
      if (clockOutTs.toMillis() <= clockInTs.toMillis()) {
        return showMsg("Clock-Out time must be after Clock-In time.", "error");
      }
      await updateDoc(snap.docs[0].ref, { clockOut: clockOutTs });
      done();
      showMsg(`Clock-Out recorded for ${getEmpName(empId)} at ${getSelectedTimeLabel()}`);
      loadTodayRecords();
    } catch {
      showMsg("Failed to record Clock-Out. Please try again.", "error");
    }
  };

  // Lunch break actions
  const doLunchOut = async () => {
    if (!empId) return showMsg("Please select an employee.", "error");
    const selectedDate = getSelectedDate();
    const done = perfMark(`ReceptionistPage.lunchOut(${getEmpName(empId)})`);
    try {
      const q = query(
        col("attendance"),
        where("empId", "==", empId),
        where("date", "==", selectedDate),
        where("clockOut", "==", null)
      );
      const snap = await getDocs(q);
      if (snap.empty) return showMsg("No active clock-in found. Clock in first.", "error");
      const data = snap.docs[0].data();
      if (data.lunchOut && !data.lunchIn) return showMsg("Already on lunch break. Record lunch-in first.", "error");
      if (data.lunchOut && data.lunchIn) return showMsg("Lunch break already recorded for today.", "error");
      await updateDoc(snap.docs[0].ref, { lunchOut: getSelectedTimestamp() });
      done();
      showMsg(`Lunch-Out recorded for ${getEmpName(empId)}`);
      loadTodayRecords();
    } catch {
      showMsg("Failed to record Lunch-Out.", "error");
    }
  };

  const doLunchIn = async () => {
    if (!empId) return showMsg("Please select an employee.", "error");
    const selectedDate = getSelectedDate();
    const done = perfMark(`ReceptionistPage.lunchIn(${getEmpName(empId)})`);
    try {
      const q = query(
        col("attendance"),
        where("empId", "==", empId),
        where("date", "==", selectedDate),
        where("clockOut", "==", null)
      );
      const snap = await getDocs(q);
      if (snap.empty) return showMsg("No active clock-in found.", "error");
      const data = snap.docs[0].data();
      if (!data.lunchOut) return showMsg("No lunch-out recorded. Record lunch-out first.", "error");
      if (data.lunchIn) return showMsg("Lunch-in already recorded.", "error");
      const lunchInTs = getSelectedTimestamp();
      if (lunchInTs.toMillis() <= (data.lunchOut as Timestamp).toMillis()) {
        return showMsg("Lunch-In time must be after Lunch-Out time.", "error");
      }
      await updateDoc(snap.docs[0].ref, { lunchIn: lunchInTs });
      done();
      showMsg(`Lunch-In recorded for ${getEmpName(empId)}`);
      loadTodayRecords();
    } catch {
      showMsg("Failed to record Lunch-In.", "error");
    }
  };

  // Change request with more details
  const submitChangeRequest = async () => {
    if (!changeTarget) return;
    if (!changeReason.trim()) return showMsg("Please provide a reason for the change request.", "error");
    try {
      await addDoc(col("changeRequests"), {
        type: "attendance_edit",
        attendanceId: changeTarget.id,
        empId: changeTarget.empId,
        empName: getEmpName(changeTarget.empId),
        date: changeTarget.date,
        currentClockIn: changeTarget.clockIn ? dayjs(changeTarget.clockIn.toDate()).format("hh:mm A") : null,
        currentClockOut: changeTarget.clockOut ? dayjs(changeTarget.clockOut.toDate()).format("hh:mm A") : null,
        requestedBy: auth.currentUser?.uid ?? "",
        reason: changeReason.trim(),
        status: "pending",
        createdAt: Timestamp.now(),
      });
      showMsg("Change request submitted. Awaiting admin approval.", "info");
      setChangeOpen(false);
      setChangeReason("");
      setChangeTarget(null);
    } catch {
      showMsg("Failed to submit change request.", "error");
    }
  };

  // ─── Bulk Update Logic ───
  const loadBulkData = useCallback(async () => {
    const active = employees.filter((e) => !e.status || e.status === "active");
    const empsForBulk = locationId
      ? active.filter((e) => (e.branchId || e.locationId) === locationId)
      : active;
    if (empsForBulk.length === 0) {
      setBulkRows([]);
      return;
    }
    // Load existing attendance for bulkDate
    try {
      const constraints: ReturnType<typeof where>[] = [where("date", "==", bulkDate)];
      if (locationId) constraints.push(where("branchId", "==", locationId));
      const q = query(col("attendance"), ...constraints);
      const snap = await timed(`ReceptionistPage.loadBulkData(${bulkDate})`, () => getDocs(q));
      const existing = snap.docs.map((d) => {
        const data = d.data() as Record<string, unknown>;
        return {
          id: d.id,
          empId: (data.empId as string) ?? "",
          clockIn: (data.clockIn as Timestamp) ?? null,
          clockOut: (data.clockOut as Timestamp) ?? null,
          lunchOut: (data.lunchOut as Timestamp) ?? null,
          lunchIn: (data.lunchIn as Timestamp) ?? null,
        };
      });
      const rows: BulkRow[] = empsForBulk.map((emp) => {
        const ex = existing.find((e) => e.empId === emp.empId);
        const name = emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : emp.fullName;
        return {
          empId: emp.empId,
          empName: name,
          clockInTime: ex?.clockIn ? dayjs(ex.clockIn.toDate()).format("HH:mm") : "",
          clockOutTime: ex?.clockOut ? dayjs(ex.clockOut.toDate()).format("HH:mm") : "",
          lunchOutTime: ex?.lunchOut ? dayjs(ex.lunchOut.toDate()).format("HH:mm") : "",
          lunchInTime: ex?.lunchIn ? dayjs(ex.lunchIn.toDate()).format("HH:mm") : "",
          existingId: ex?.id ?? null,
          hasExisting: !!ex,
        };
      });
      setBulkRows(rows);
      setOriginalBulkRows(rows.map((r) => ({ ...r })));
      bulkPag.resetPage();
    } catch {
      showMsg("Failed to load bulk data.", "error");
    }
  }, [locationId, bulkDate, employees]);

  useEffect(() => {
    if (viewMode === 1) loadBulkData();
  }, [viewMode, loadBulkData]);

  const updateBulkRow = (idx: number, field: keyof BulkRow, value: string) => {
    const realIdx = bulkPag.page * bulkPag.rowsPerPage + idx;
    setBulkRows((prev) => prev.map((r, i) => i === realIdx ? { ...r, [field]: value } : r));
  };

  const hasBulkPageDirty = (): boolean => {
    const start = bulkPag.page * bulkPag.rowsPerPage;
    const end = start + bulkPag.rowsPerPage;
    for (let i = start; i < end && i < bulkRows.length; i++) {
      const row = bulkRows[i];
      const orig = originalBulkRows.find((o) => o.empId === row.empId);
      if (!orig) continue;
      if (orig.clockInTime !== row.clockInTime || orig.clockOutTime !== row.clockOutTime
          || orig.lunchOutTime !== row.lunchOutTime || orig.lunchInTime !== row.lunchInTime) return true;
    }
    return false;
  };

  const handleBulkPageChange = (_: unknown, newPage: number) => {
    if (hasBulkPageDirty()) {
      setPendingBulkPage({ page: newPage });
    } else {
      bulkPag.onPageChange(_, newPage);
    }
  };

  const handleBulkRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (hasBulkPageDirty()) {
      setPendingBulkPage({ page: 0, rpp: parseInt(e.target.value, 10) });
    } else {
      bulkPag.onRowsPerPageChange(e);
    }
  };

  const confirmBulkPageChange = () => {
    if (pendingBulkPage) {
      if (pendingBulkPage.rpp !== undefined) {
        bulkPag.onRowsPerPageChange({ target: { value: String(pendingBulkPage.rpp) } } as React.ChangeEvent<HTMLInputElement>);
      } else {
        bulkPag.onPageChange(null, pendingBulkPage.page);
      }
      setPendingBulkPage(null);
    }
  };

  const saveBulkUpdate = async () => {
    if (bulkDate > today) return showMsg("Cannot save timesheet for a future date.", "error");
    if (dayjs(today).diff(dayjs(bulkDate), "day") > timesheetEditDays) return showMsg(`Cannot save timesheet for dates older than ${timesheetEditDays} days.`, "error");
    setBulkSaving(true);
    const doneBulk = perfMark(`ReceptionistPage.saveBulkUpdate(${bulkDate})`);
    let saved = 0;
    let errors = 0;
    for (const row of bulkRows) {
      // Skip empty rows
      if (!row.clockInTime && !row.clockOutTime && !row.lunchOutTime && !row.lunchInTime) continue;
      // Skip unchanged rows
      const orig = originalBulkRows.find((o) => o.empId === row.empId);
      if (orig && orig.clockInTime === row.clockInTime && orig.clockOutTime === row.clockOutTime
          && orig.lunchOutTime === row.lunchOutTime && orig.lunchInTime === row.lunchInTime) continue;
      // Clock-out requires clock-in
      if (row.clockOutTime && !row.clockInTime) {
        showMsg(`${row.empName}: Cannot enter clock-out without clock-in. Skipped.`, "error");
        errors++;
        continue;
      }
      // Lunch times require clock-in
      if ((row.lunchOutTime || row.lunchInTime) && !row.clockInTime) {
        showMsg(`${row.empName}: Cannot enter lunch times without clock-in. Skipped.`, "error");
        errors++;
        continue;
      }
      // Lunch-in requires lunch-out
      if (row.lunchInTime && !row.lunchOutTime) {
        showMsg(`${row.empName}: Cannot enter lunch-out without lunch-in. Skipped.`, "error");
        errors++;
        continue;
      }
      // Block employees on leave
      if (isOnLeave(row.empId, bulkDate)) {
        showMsg(`${row.empName}: On approved/pending leave. Skipped.`, "error");
        errors++;
        continue;
      }
      try {
        const makeTs = (time: string) => {
          if (!time) return null;
          return Timestamp.fromDate(dayjs(`${bulkDate} ${time}`, "YYYY-MM-DD HH:mm").toDate());
        };
        const clockIn = makeTs(row.clockInTime);
        const clockOut = makeTs(row.clockOutTime);
        const lunchOut = makeTs(row.lunchOutTime);
        const lunchIn = makeTs(row.lunchInTime);
        // Validate time order: Clock In < Lunch In < Lunch Out < Clock Out
        if (clockIn && lunchOut && lunchOut.toMillis() <= clockIn.toMillis()) {
          showMsg(`${row.empName}: Lunch In must be after Clock In. Skipped.`, "error");
          errors++;
          continue;
        }
        if (lunchOut && lunchIn && lunchIn.toMillis() <= lunchOut.toMillis()) {
          showMsg(`${row.empName}: Lunch Out must be after Lunch In. Skipped.`, "error");
          errors++;
          continue;
        }
        if (lunchIn && clockOut && clockOut.toMillis() <= lunchIn.toMillis()) {
          showMsg(`${row.empName}: Clock Out must be after Lunch Out. Skipped.`, "error");
          errors++;
          continue;
        }
        if (clockIn && clockOut && clockOut.toMillis() <= clockIn.toMillis()) {
          showMsg(`${row.empName}: Clock Out must be after Clock In. Skipped.`, "error");
          errors++;
          continue;
        }
        const emp = employees.find((e) => e.empId === row.empId);
        const docData = {
          empId: row.empId,
          branchId: locationId || emp?.branchId || "",
          date: bulkDate,
          clockIn,
          clockOut,
          lunchOut,
          lunchIn,
          createdBy: auth.currentUser?.uid ?? "",
          verified: false,
        };
        if (row.existingId) {
          await updateDoc(docRef("attendance", row.existingId), docData);
        } else {
          await addDoc(col("attendance"), docData);
        }
        saved++;
      } catch {
        errors++;
      }
    }
    setBulkSaving(false);
    doneBulk();
    if (saved > 0) showMsg(`Saved ${saved} attendance record(s).${errors > 0 ? ` ${errors} error(s).` : ""}`);
    else if (errors > 0) showMsg(`${errors} error(s) occurred.`, "error");
    else showMsg("No changes to save.", "info");
    loadBulkData();
    loadTodayRecords();
  };

  const formatTs = (ts: Timestamp | null) => (ts ? dayjs(ts.toDate()).format("hh:mm:ss A") : "—");

  // Calculate work hours accounting for lunch
  const calcHours = (r: AttendanceRow): string => {
    if (!r.clockIn || !r.clockOut) return "—";
    let ms = r.clockOut.toDate().getTime() - r.clockIn.toDate().getTime();
    if (r.lunchOut && r.lunchIn) {
      ms -= (r.lunchIn.toDate().getTime() - r.lunchOut.toDate().getTime());
    }
    return (ms / 3600000).toFixed(1);
  };

  const hoursToDay = (h: string): string => {
    if (h === "—") return "";
    return " (1 day)";
  };

  const pageTitle = role === "admin" ? "Timesheet Management" : "Timesheet";
  const missingPunchRecords = todayRecords.filter((r) => {
    if (!r.clockIn || r.clockOut) return false;
    const clockInMs = r.clockIn.toMillis();
    return Date.now() - clockInMs > 24 * 60 * 60 * 1000;
  });

  return (
    <Box>
      {/* Missing-Punch Banner */}
      {missingPunchRecords.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "warning.light",
            color: "warning.contrastText",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <WarningAmberIcon color="warning" />
          <Typography variant="body2" sx={{ fontWeight: 600, color: "warning.dark", flex: 1 }}>
            Missing Clock-Out: {missingPunchRecords.map((r) => getEmpName(r.empId)).join(", ")}
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {pageTitle}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccessTimeIcon color="primary" fontSize="small" />
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
            {currentTime.format("hh:mm:ss A")} — {currentTime.format("DD/MMM/YYYY")}
          </Typography>
          <Chip label="Live" size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
        </Box>
      </Box>

      {/* View Mode Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={viewMode} onChange={(_, v) => {
          setViewMode(v);
          if (v === 1) {
            setLocationId("");
          } else {
            const lastLoc = localStorage.getItem(LS_LAST_LOC);
            if (lastLoc && locations.some((l) => l.id === lastLoc)) setLocationId(lastLoc);
            const lastEmp = localStorage.getItem(LS_LAST_EMP);
            if (lastEmp) setEmpId(lastEmp);
          }
        }} sx={{ borderBottom: 1, borderColor: "divider", px: 1 }}>
          <Tab icon={<PersonIcon />} iconPosition="start" label="Individual" sx={{ textTransform: "none", fontWeight: 600 }} />
          <Tab icon={<GroupIcon />} iconPosition="start" label="Bulk Update" sx={{ textTransform: "none", fontWeight: 600 }} />
        </Tabs>
      </Paper>

      {/* ═══════ INDIVIDUAL MODE ═══════ */}
      {viewMode === 0 && (
        <>
          {/* Selection & Date/Time */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Select Employee</Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <FormControl sx={{ minWidth: 250, flex: 1 }}>
                <InputLabel>Location</InputLabel>
                <Select
                  value={locationId}
                  label="Location"
                  onChange={(e: SelectChangeEvent) => handleLocationChange(e.target.value)}
                >
                  <MenuItem value=""><em>All Locations</em></MenuItem>
                  {sortedLocations.map((l) => (
                    <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 250, flex: 1 }}>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={empId}
                  label="Employee"
                  onChange={(e: SelectChangeEvent) => handleEmpChange(e.target.value)}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                >
                  {filteredEmployees.map((emp) => (
                    <MenuItem key={emp.empId} value={emp.empId}>
                      {emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : emp.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Date & Time */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ScheduleIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Date & Time</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant={!useCustomTime ? "contained" : "outlined"}
                  startIcon={<TodayIcon />}
                  onClick={() => setUseCustomTime(false)}
                  sx={{ textTransform: "none" }}
                >
                  Current Date & Time
                </Button>
                <Button
                  size="small"
                  variant={useCustomTime ? "contained" : "outlined"}
                  startIcon={<ScheduleIcon />}
                  onClick={() => {
                    if (!useCustomTime) {
                      setUseCustomTime(true);
                      setCustomDate(dayjs().format("YYYY-MM-DD"));
                      setCustomTime(dayjs().format("HH:mm"));
                    }
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Pick Date & Time
                </Button>
              </Box>
            </Box>
            {useCustomTime && (
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <DatePicker
                  label="Date"
                  value={dayjs(customDate)}
                  onChange={(v: Dayjs | null) => v && setCustomDate(v.format("YYYY-MM-DD"))}
                  format="DD/MMM/YYYY"
                  maxDate={tsMaxDate}
                  minDate={tsMinDate}
                  slotProps={{ textField: { sx: { minWidth: 180 } } }}
                />
                <TextField
                  type="time"
                  label="Time"
                  InputLabelProps={{ shrink: true }}
                  value={customTime}
                  onChange={(e) => setCustomTime(e.target.value)}
                  sx={{ minWidth: 160 }}
                />
              </Box>
            )}
          </Paper>

          {/* Clock In/Out + Lunch Cards — chronological order */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", justifyContent: "center" }}>
            <Card
              sx={{
                flex: "0 1 240px",
                cursor: !empId ? "not-allowed" : "pointer",
                opacity: !empId ? 0.5 : 1,
                transition: "all 0.2s",
                ...(empId ? { "&:hover": { transform: "translateY(-2px)", boxShadow: 4 } } : {}),
              }}
              onClick={empId ? requestClockIn : undefined}
            >
              <CardContent sx={{ textAlign: "center", py: 3 }}>
                <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: "secondary.main", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                  <LoginIcon sx={{ fontSize: 28, color: "#fff" }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Clock In</Typography>
                <Typography variant="caption" color="text.secondary">Record arrival</Typography>
              </CardContent>
            </Card>

            <Card
              sx={{
                flex: "0 1 240px",
                cursor: !empId ? "not-allowed" : "pointer",
                opacity: !empId ? 0.5 : 1,
                transition: "all 0.2s",
                ...(empId ? { "&:hover": { transform: "translateY(-2px)", boxShadow: 4 } } : {}),
              }}
              onClick={empId ? doLunchOut : undefined}
            >
              <CardContent sx={{ textAlign: "center", py: 3 }}>
                <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: "warning.main", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                  <RestaurantIcon sx={{ fontSize: 28, color: "#fff" }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Lunch In</Typography>
                <Typography variant="caption" color="text.secondary">Going for lunch</Typography>
              </CardContent>
            </Card>

            <Card
              sx={{
                flex: "0 1 240px",
                cursor: !empId ? "not-allowed" : "pointer",
                opacity: !empId ? 0.5 : 1,
                transition: "all 0.2s",
                ...(empId ? { "&:hover": { transform: "translateY(-2px)", boxShadow: 4 } } : {}),
              }}
              onClick={empId ? doLunchIn : undefined}
            >
              <CardContent sx={{ textAlign: "center", py: 3 }}>
                <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: "secondary.main", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                  <RestaurantIcon sx={{ fontSize: 28, color: "#fff" }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Lunch Out</Typography>
                <Typography variant="caption" color="text.secondary">Back from lunch</Typography>
              </CardContent>
            </Card>

            <Card
              sx={{
                flex: "0 1 240px",
                cursor: !empId ? "not-allowed" : "pointer",
                opacity: !empId ? 0.5 : 1,
                transition: "all 0.2s",
                ...(empId ? { "&:hover": { transform: "translateY(-2px)", boxShadow: 4 } } : {}),
              }}
              onClick={empId ? requestClockOut : undefined}
            >
              <CardContent sx={{ textAlign: "center", py: 3 }}>
                <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: "error.main", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                  <LogoutIcon sx={{ fontSize: 28, color: "#fff" }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Clock Out</Typography>
                <Typography variant="caption" color="text.secondary">Record departure</Typography>
              </CardContent>
            </Card>
          </Box>
        </>
      )}

      {/* ═══════ BULK UPDATE MODE ═══════ */}
      {viewMode === 1 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Bulk Timesheet Entry</Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <BulkDatePicker
                value={bulkDate}
                onChange={setBulkDate}
                minDate={tsMinDate}
                maxDate={tsMaxDate}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<TodayIcon />}
                onClick={() => setBulkDate(today)}
                disabled={bulkDate === today}
                sx={{ textTransform: "none", height: 40 }}
              >
                Today
              </Button>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Location</InputLabel>
                <Select value={locationId} label="Location" onChange={(e: SelectChangeEvent) => handleLocationChange(e.target.value)}>
                  <MenuItem value=""><em>All Locations</em></MenuItem>
                  {sortedLocations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveBulkUpdate}
                disabled={bulkSaving}
              >
                {bulkSaving ? "Saving..." : "Save All"}
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter clock-in/out and lunch times for all employees at once. Leave fields empty to skip.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell align="center">Clock In</TableCell>
                  <TableCell align="center">Lunch In</TableCell>
                  <TableCell align="center">Lunch Out</TableCell>
                  <TableCell align="center">Clock Out</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={99} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      {locationId ? "No employees at this location." : "No employees found."}
                    </TableCell>
                  </TableRow>
                )}
                {bulkPag.paginatedItems.map((row, idx) => (
                  <TableRow key={row.empId}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: "primary.main" }}>{row.empName.charAt(0)}</Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.empName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="time"
                        size="small"
                        value={row.clockInTime}
                        onChange={(e) => updateBulkRow(idx, "clockInTime", e.target.value)}
                        sx={{ width: 130 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="time"
                        size="small"
                        value={row.lunchOutTime}
                        onChange={(e) => updateBulkRow(idx, "lunchOutTime", e.target.value)}
                        sx={{ width: 130 }}
                        InputLabelProps={{ shrink: true }}
                        placeholder="Lunch In"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="time"
                        size="small"
                        value={row.lunchInTime}
                        onChange={(e) => updateBulkRow(idx, "lunchInTime", e.target.value)}
                        sx={{ width: 130 }}
                        InputLabelProps={{ shrink: true }}
                        placeholder="Lunch Out"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="time"
                        size="small"
                        value={row.clockOutTime}
                        onChange={(e) => updateBulkRow(idx, "clockOutTime", e.target.value)}
                        sx={{ width: 130 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {row.hasExisting
                        ? <Chip label="Existing" size="small" color="info" variant="outlined" sx={{ fontWeight: 600 }} />
                        : row.clockInTime
                          ? <Chip label="New" size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} />
                          : <Chip label="Empty" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {bulkRows.length > 0 && (
            <TablePagination
              component="div"
              count={bulkPag.totalCount}
              page={bulkPag.page}
              rowsPerPage={bulkPag.rowsPerPage}
              onPageChange={handleBulkPageChange}
              onRowsPerPageChange={handleBulkRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50]}
            />
          )}
        </Paper>
      )}

      {/* Unsaved bulk data warning dialog */}
      <Dialog open={!!pendingBulkPage} onClose={() => setPendingBulkPage(null)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>You have unsaved changes on this page. If you navigate away, your changes will be lost. Do you want to continue?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingBulkPage(null)}>Stay</Button>
          <Button onClick={confirmBulkPageChange} color="error" variant="contained">Discard & Continue</Button>
        </DialogActions>
      </Dialog>

      {/* Attendance Records */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTimeIcon color="primary" />
            <Typography variant="h6">{(() => {
                const d = viewMode === 1 ? bulkDate : getSelectedDate();
                if (d === today) return "Today's Records";
                if (d === dayjs().subtract(1, "day").format("YYYY-MM-DD")) return "Yesterday's Records";
                return `Records for ${dayjs(d).format("DD MMM YYYY")}`;
              })()}</Typography>
          </Box>
          <Chip label={`${todayRecords.length} entries`} size="small" variant="outlined" />
        </Box>
        <Divider sx={{ mb: 2 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell align="center">Clock In</TableCell>
                <TableCell align="center">Lunch In</TableCell>
                <TableCell align="center">Lunch Out</TableCell>
                <TableCell align="center">Clock Out</TableCell>
                <TableCell align="center">Hours</TableCell>
                <TableCell align="center">Status</TableCell>
                {role === "receptionist" && <TableCell align="center">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {todayRecords.length === 0 && (
                <TableRow>
                  <TableCell colSpan={99} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    {viewMode === 1 ? (bulkDate === dayjs().subtract(1, "day").format("YYYY-MM-DD") ? "No attendance records for yesterday yet." : `No attendance records for ${dayjs(bulkDate).format("DD MMM YYYY")} yet.`) : "No attendance records for today yet."}
                  </TableCell>
                </TableRow>
              )}
              {recPag.paginatedItems.map((r) => {
                const hours = calcHours(r);
                return (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: "primary.main" }}>
                          {getEmpName(r.empId).charAt(0)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{getEmpName(r.empId)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={formatTs(r.clockIn)} size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} />
                    </TableCell>
                    <TableCell align="center">
                      {r.lunchOut ? <Chip label={formatTs(r.lunchOut)} size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} /> : "—"}
                    </TableCell>
                    <TableCell align="center">
                      {r.lunchIn ? <Chip label={formatTs(r.lunchIn)} size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} /> : "—"}
                    </TableCell>
                    <TableCell align="center">
                      {r.clockOut
                        ? <Chip label={formatTs(r.clockOut)} size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} />
                        : <Chip label="Active" size="small" color="warning" sx={{ fontWeight: 600 }} />}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: hours !== "—" && parseFloat(hours) < 8 ? "warning.main" : "inherit" }}>
                      {hours}{hours !== "—" ? `h${hoursToDay(hours)}` : ""}
                    </TableCell>
                    <TableCell align="center">
                      {r.verified
                        ? <Chip label="Verified" size="small" color="success" sx={{ fontWeight: 600 }} />
                        : <Chip label="Pending" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />}
                    </TableCell>
                    {role === "receptionist" && (
                      <TableCell align="center">
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => { setChangeTarget(r); setChangeOpen(true); }}
                          sx={{ textTransform: "none", fontSize: "0.75rem" }}
                        >
                          Request Change
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {todayRecords.length > 0 && (
          <TablePagination component="div" count={recPag.totalCount} page={recPag.page} onPageChange={recPag.onPageChange} rowsPerPage={recPag.rowsPerPage} onRowsPerPageChange={recPag.onRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]} />
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Timesheet Entry</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2">
              <strong>Action:</strong> {confirmAction === "clockIn" ? "Clock In" : "Clock Out"}
            </Typography>
            <Typography variant="body2">
              <strong>Employee:</strong> {getEmpName(empId)}
            </Typography>
            <Typography variant="body2">
              <strong>Location:</strong> {locations.find((l) => l.id === locationId)?.name || "All"}
            </Typography>
            <Typography variant="body2">
              <strong>Date:</strong> {dayjs(getSelectedDate()).format("DD/MMM/YYYY")}
            </Typography>
            <Typography variant="body2">
              <strong>Time:</strong> {getSelectedTimeLabel()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Change Request Dialog */}
      <Dialog open={changeOpen} onClose={() => setChangeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Request Attendance Change</DialogTitle>
        <DialogContent>
          {changeTarget && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Employee: <strong>{getEmpName(changeTarget.empId)}</strong> | Date: <strong>{dayjs(changeTarget.date).format("DD/MMM/YYYY")}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clock In: {formatTs(changeTarget.clockIn)} | Clock Out: {formatTs(changeTarget.clockOut)}
              </Typography>
              {(changeTarget.lunchOut || changeTarget.lunchIn) && (
                <Typography variant="body2" color="text.secondary">
                  Lunch Out: {formatTs(changeTarget.lunchOut)} | Lunch In: {formatTs(changeTarget.lunchIn)}
                </Typography>
              )}
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for change"
            placeholder="Describe what needs to be corrected and why..."
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setChangeOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitChangeRequest}>Submit Request</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!msg} autoHideDuration={4000} onClose={() => setMsg("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={msgType} variant="filled" onClose={() => setMsg("")}>{msg}</Alert>
      </Snackbar>
    </Box>
  );
}
